import React, { useState } from "react";
import { Link } from "react-router-dom";
import { subscribeNewsletter } from "../../utils/posts";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin, isApproved } = useAuth();
  const isMember = isAdmin || isApproved;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email.");
    setSubmitting(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list.");
      setEmail("");
    } catch {
      toast.error("Something went wrong.");
    } finally { setSubmitting(false); }
  };

  const publicLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
    { to: "/login", label: "Login" },
  ];

  const memberLinks = [
    { to: "/insights", label: "Insights" },
    { to: "/weekly-outlook", label: "Weekly Outlook" },
    { to: "/trade-ideas", label: "Trade Ideas" },
    { to: "/education", label: "Education" },
    { to: "/profile", label: "Profile" },
  ];

  const links = isMember ? memberLinks : publicLinks;

  return (
    <footer style={{ background: "#060606", borderTop: "1px solid #141414", position: "relative", overflow: "hidden" }}>

      {/* Ambient glow */}
      <div style={{ position: "absolute", bottom: -60, left: "20%", width: 500, height: 200, borderRadius: "50%", background: "radial-gradient(ellipse,#c9a84c06 0%,transparent 70%)", pointerEvents: "none", animation: "glowOrb 8s ease-in-out infinite" }} />
      <div style={{ position: "absolute", bottom: -40, right: "10%", width: 300, height: 150, borderRadius: "50%", background: "radial-gradient(ellipse,#818cf806 0%,transparent 70%)", pointerEvents: "none", animation: "glowOrb 6s ease-in-out infinite reverse" }} />

      {/* Top line glow */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, #c9a84c30, #f5c51840, #c9a84c30, transparent)", animation: "shimmer 4s ease-in-out infinite" }} />

      <div className="container" style={{ position: "relative", zIndex: 1, padding: "32px 24px 24px" }}>

        {/* Main row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 24, marginBottom: 24 }}>

          {/* Brand */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 10px #4ade80", animation: "dotBlink 2s ease-in-out infinite", flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1rem", color: "#f0f0f0", letterSpacing: "0.06em", textTransform: "uppercase", animation: "logoGlow 4s ease-in-out infinite" }}>Macro Trader</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "8px", letterSpacing: "0.2em", color: "#c9a84c", textTransform: "uppercase" }}>Research & Analysis</div>
            </div>
          </div>

          {/* Nav links */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {links.map((l, i) => (
              <Link key={l.to} to={l.to}
                style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#333", padding: "5px 12px", borderRadius: "20px", border: "1px solid transparent", transition: "all 0.2s", textDecoration: "none" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#c9a84c"; e.currentTarget.style.borderColor = "#c9a84c28"; e.currentTarget.style.background = "#c9a84c08"; e.currentTarget.style.boxShadow = "0 0 12px #c9a84c15"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#333"; e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; e.currentTarget.style.boxShadow = "none"; }}
              >{l.label}</Link>
            ))}
          </div>

          {/* Email subscribe */}
          <form onSubmit={handleSubscribe} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ background: "#0a0a0a", border: "1px solid #1e1e1e", borderRadius: "6px", padding: "8px 14px", fontSize: "12px", color: "#888", fontFamily: "var(--font-mono)", outline: "none", width: 180, transition: "border-color 0.2s, box-shadow 0.2s" }}
              onFocus={e => { e.target.style.borderColor = "#c9a84c40"; e.target.style.boxShadow = "0 0 12px #c9a84c10"; }}
              onBlur={e => { e.target.style.borderColor = "#1e1e1e"; e.target.style.boxShadow = "none"; }}
            />
            <button type="submit" disabled={submitting}
              style={{ background: "#c9a84c15", border: "1px solid #c9a84c35", borderRadius: "6px", padding: "8px 16px", fontSize: "10px", fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", color: "#c9a84c", cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#c9a84c25"; e.currentTarget.style.boxShadow = "0 0 16px #c9a84c20"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#c9a84c15"; e.currentTarget.style.boxShadow = "none"; }}
            >
              {submitting ? "..." : "Subscribe"}
            </button>
          </form>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: "1px solid #0e0e0e", paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <p style={{ fontSize: "10px", color: "#222", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}>
            © {new Date().getFullYear()} Macro Trader. All content for educational purposes only. Not financial advice.
          </p>
          <p style={{ fontSize: "10px", color: "#c9a84c40", fontFamily: "var(--font-serif)", fontStyle: "italic", letterSpacing: "0.04em", animation: "shimmer 5s ease-in-out infinite" }}>
            "Learn first, then earn."
          </p>
        </div>
      </div>

      <style>{`
        @media(max-width:900px){
          footer .container > div:first-child { flex-direction: column; align-items: flex-start !important; }
        }
      `}</style>
    </footer>
  );
}
